import type { CareDefinitions, CareSectionSummary, CareSymptom, CareTrendAnswer, CareTrendPoint } from '@/types/care'

/**
 * The symptoms a section was worst on at one check-in, keyed by question hash.
 *
 * "Highest value selected in each section" reads the section one answer at a time and keeps the
 * ties at the top: a section scored 3/2/3/0 surfaces the two threes, which are the answers driving
 * that section's score. A section the patient reported nothing on has no worst symptom — every
 * answer sits at zero — so it surfaces none rather than listing the whole section at its floor.
 */
const worstAnswers = (answers: CareTrendAnswer[], definitions: CareDefinitions, calculatorHash: string) => {
  const inSection = answers.filter(
    answer => definitions.questions[answer.questionHash]?.calculatorHash === calculatorHash && answer.score !== null
  )

  const highest = inSection.reduce((worst, answer) => Math.max(worst, answer.score ?? 0), 0)
  if (highest <= 0) return new Map<string, CareTrendAnswer>()

  return new Map(inSection.filter(answer => answer.score === highest).map(answer => [answer.questionHash, answer]))
}

/**
 * Every scored answer at one check-in, keyed by question — the baseline a later check-in is
 * compared against. Every answer, not just the section's worst: whether a symptom got worse is a
 * question about that same question's own score, so the comparison needs the score of a question
 * that was nowhere near the maximum last time.
 */
const scoresByQuestion = (answers: CareTrendAnswer[]) =>
  new Map(answers.filter(answer => answer.score !== null).map(answer => [answer.questionHash, answer.score as number]))

const toSymptom = (answer: CareTrendAnswer, definitions: CareDefinitions, isWorse: boolean): CareSymptom => {
  const question = definitions.questions[answer.questionHash]
  const option = answer.optionHash ? question?.options[answer.optionHash] : undefined

  return {
    questionHash: answer.questionHash,
    // The pill names the *symptom*, which is the question ("Hot flushes"), not the option — the
    // option is the severity, and it is already carried by the score. Falls back through the
    // option label to the raw answer so an unresolved definition still reads as something.
    label: question?.title ?? option?.label ?? String(answer.value ?? '—'),
    score: answer.score ?? 0,
    isWorse
  }
}

/**
 * The table under the chart: one row per section, for the most recent check-in.
 *
 * `previous` is the check-in before it, and the only thing it decides is which pills draw red.
 * A pill is red when *that question's own score went up*, which is the only reading of "symptom
 * worse" that holds: membership of the section's worst set is not, because the set's membership
 * moves when another question falls. Previously 3/2 and now 1/2 makes the unchanged 2 the
 * section's worst without it having worsened, while a 2 rising to 3 stays in the set throughout
 * and would never be flagged.
 *
 * The first ever check-in has no previous, so nothing is marked: everything is a starting
 * observation rather than a deterioration.
 */
export const toSectionSummaries = (points: CareTrendPoint[], definitions: CareDefinitions): CareSectionSummary[] => {
  const ordered = [...points].sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime())
  const latest = ordered.at(-1)
  const previous = ordered.at(-2)
  if (!latest) return []

  const before = previous ? scoresByQuestion(previous.answers ?? []) : null

  return definitions.calculators.map(calculator => {
    const current = worstAnswers(latest.answers ?? [], definitions, calculator.hash)

    return {
      key: calculator.hash,
      name: calculator.name,
      color: calculator.color,
      value: latest.scores.values?.[calculator.hash] ?? null,
      max: calculator.max,
      symptoms: [...current.values()]
        .map(answer => {
          const priorScore = before?.get(answer.questionHash)
          // A question the previous check-in never asked has nothing to compare against, so it
          // counts as worse only by appearing at all — it is new, and it is being reported.
          const isWorse =
            before !== null && (priorScore === undefined ? (answer.score ?? 0) > 0 : (answer.score ?? 0) > priorScore)

          return toSymptom(answer, definitions, isWorse)
        })
        // Worst first, then alphabetically so the row does not reshuffle between check-ins.
        .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
    }
  })
}

/**
 * The most recent check-in, or null when there are none.
 *
 * By date, never by position: the read is newest-first, the adapters sort ascending, and a caller
 * that reaches for `points.at(-1)` gets whichever end the last hand to touch the array left there.
 */
export const latestCheckIn = (points: CareTrendPoint[]): CareTrendPoint | null =>
  points.reduce<CareTrendPoint | null>((newest, point) => {
    if (!newest) return point
    return new Date(point.submittedAt) > new Date(newest.submittedAt) ? point : newest
  }, null)

/** The date the table is describing. */
export const latestCheckInDate = (points: CareTrendPoint[]): Date | null => {
  const latest = latestCheckIn(points)
  return latest ? new Date(latest.submittedAt) : null
}
